import moment from "moment";
import {
  coverageOf,
  formatDay,
  nextDeductionMonth,
  pickBand,
  runCalculation,
  sampleSites,
  setDateLabel,
} from "pages/PayRoll/Configuration/StatutoryConfiguration/LWF/lwfConfigData";

// Payroll > Statutory > Labour Welfare Fund > Employees and the LWF
// Reports, on sample data until the LWF APIs exist (LWF Specification v1.4
// §B4–B5, screens LWF2–LWF6a). Coverage is worked out here from the LWF
// Configuration sample with the spec's order of work (B5.1); the payroll
// engine will do this later. Nothing is typed on the Employees screen.

export { formatDay };

export const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;
export const num = (value) => Number(value || 0).toLocaleString("en-IN");
const iso = (value) => moment(value).format("YYYY-MM-DD");

// ---- Sites and versions (from the Configuration sample) ----

export const SITES = sampleSites();
export const siteByName = (name) => SITES.find((site) => site.name === name) || null;
export const SITE_OPTIONS = [{ value: "all", label: "All sites" }, ...SITES.map((site) => ({ value: site.name, label: site.name }))];

// The saved version in force on a date (the whole month uses the one in
// force on the 1st).
export const versionOn = (site, date) =>
  (site?.versions || [])
    .filter((version) => version.status !== "Draft" && version.effective_from <= date && (!version.effective_to || version.effective_to >= date))
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] || null;

// ---- Employees ----

const person = (emp_id, name, department, site, category, wage, extra = {}) => ({
  id: emp_id,
  emp_id,
  name,
  department,
  site,
  category,
  wage,
  doj: "2021-04-01",
  lwd: null,
  ...extra,
});

// The spec's and mockups' sample people. Vimal R had no pay in Dec 2025;
// Karthik S left on 20 Dec 2025, before the 31 Dec set date.
const NAMED = [
  person("E001", "Ravi Kumar", "Production", "Erode Plant", "Worker", 42000, { doj: "2019-06-03" }),
  person("E002", "Anjali Sharma", "Production", "Erode Plant", "Worker", 22500, { doj: "2021-01-04" }),
  person("E004", "Priya N", "Production", "Chennai Office", "Worker", 6250, { doj: "2023-03-06" }),
  person("E120", "Kannan V", "Sales", "Bengaluru Sales Office", "Staff", 28000, { doj: "2022-07-11" }),
  person("E150", "Arjun M", "Operations", "Chennai Office", "Manager", 85000, { doj: "2020-02-17" }),
  person("E091", "Rajan M", "Stores", "Erode Plant", "Worker", 18000, { doj: "2018-09-24" }),
  person("E161", "Meena J", "Production", "Erode Plant", "", 12000, { doj: "2025-08-04" }),
  person("E130", "Selvi K", "Stores", "Coimbatore Depot", "Worker", 14000, { doj: "2024-05-13" }),
  person("E170", "Vimal R", "Production", "Erode Plant", "Worker", 9500, { doj: "2023-11-20", wages: { "2025-12": 0 } }),
  person("E095", "Divya K", "Accounts", "Erode Office", "Worker", 15500, { doj: "2022-12-01" }),
  person("E015", "Karthik S", "Production", "Erode Plant", "Worker", 16000, { doj: "2022-01-10", lwd: "2025-12-20", exit_reason: "Resignation" }),
];

const FIRST_NAMES = ["Arul", "Bala", "Chitra", "Dinesh", "Elango", "Fathima", "Ganesh", "Hari", "Indira", "Jaya", "Kumar", "Latha", "Mani", "Nirmala", "Prakash", "Ramya", "Saravanan", "Thilaga", "Uma", "Vel"];
const LAST_INITIALS = ["A", "B", "C", "D", "G", "K", "M", "N", "P", "R", "S", "T", "V"];
const DEPARTMENTS = {
  "Chennai Office": ["Operations", "Accounts", "HR", "Sales"],
  "Erode Plant": ["Production", "Production", "Quality", "Maintenance", "Stores"],
  "Erode Office": ["Accounts", "HR", "Operations"],
  "Bengaluru Sales Office": ["Sales"],
  "Coimbatore Depot": ["Stores", "Operations"],
};

// Filler groups so the lists show the mockup's counts: 162 employees, 150
// covered (Chennai 30, Erode Plant 92, Erode Office 19, Bengaluru 9),
// 6 not covered (5 managers + Meena held) and 6 at Coimbatore Depot.
const GROUPS = [
  { site: "Chennai Office", count: 29, category: "Staff" },
  { site: "Chennai Office", count: 1, category: "Manager" },
  { site: "Erode Plant", count: 88, category: "Worker" },
  { site: "Erode Plant", count: 3, category: "Manager" },
  { site: "Erode Office", count: 18, category: "Staff" },
  { site: "Bengaluru Sales Office", count: 8, category: "Staff" },
  { site: "Coimbatore Depot", count: 5, category: "Worker" },
];

const fillers = () => {
  let index = 0;
  return GROUPS.flatMap((group) =>
    Array.from({ length: group.count }, () => {
      index += 1;
      const departments = DEPARTMENTS[group.site];
      const wage = group.category === "Manager" ? 60000 + ((index * 1373) % 40) * 1000 : 9000 + ((index * 137) % 300) * 100;
      return person(
        `E${String(200 + index).padStart(3, "0")}`,
        `${FIRST_NAMES[index % FIRST_NAMES.length]} ${LAST_INITIALS[(index * 7) % LAST_INITIALS.length]}`,
        departments[index % departments.length],
        group.site,
        group.category,
        wage,
        { doj: iso(moment("2015-01-05").add((index * 37) % 3800, "days")) },
      );
    }),
  );
};

export const sampleEmployees = () => JSON.parse(JSON.stringify([...NAMED, ...fillers()]));
export const DEPARTMENT_LIST = [...new Set([...NAMED, ...fillers()].map((employee) => employee.department))].sort();

// Monthly wage for a payroll month (a month-specific entry wins).
const wageFor = (employee, monthKey) => (employee.wages && employee.wages[monthKey] != null ? employee.wages[monthKey] : employee.wage);

// LWF_WAGE from the version's calculation components (GROSS_E by default).
export const lwfWageOf = (version, wage) => {
  const values = runCalculation(version.components, { GROSS: wage, GROSS_E: wage, BASIC: 0, BASIC_E: 0, DA: 0, DA_E: 0, HRA: 0, HRA_E: 0, SPECIAL: 0, SPECIAL_E: 0, OT: 0, SHIFT: 0, INCENTIVE: 0 });
  return values.LWF_WAGE ?? wage;
};

// ---- Statuses ----

export const STATUS_INFO = {
  DEDUCTED: { label: "Deducted", tone: "green" },
  EE_OUTSTANDING: { label: "Employee share outstanding", tone: "amber" },
  EE_PAID_BY_COMPANY: { label: "Paid by company", tone: "purple" },
  HELD_MISSING_DATA: { label: "Held · category missing", tone: "red" },
  NOT_COVERED_CATEGORY: { label: "Not covered", tone: "slate" },
  NOT_COVERED_WAGE: { label: "Not covered · wage", tone: "slate" },
  NOT_ON_ROLLS: { label: "Not on rolls", tone: "slate" },
  SITE_NOT_SET_UP: { label: "Site not set up", tone: "red" },
  NOT_DEDUCTION_MONTH: { label: "Not a deduction month", tone: "slate" },
  NOT_APPLICABLE: { label: "Site not applicable", tone: "slate" },
  EXEMPT_SITE: { label: "Site exempt", tone: "slate" },
};
export const COVERED_STATUSES = ["DEDUCTED", "EE_OUTSTANDING", "EE_PAID_BY_COMPANY"];

const statusLabel = (status, employee, monthStart) => {
  if (status === "NOT_COVERED_CATEGORY") return `Not covered · ${employee.category}`;
  if (status === "NOT_ON_ROLLS") return `Not on rolls in ${monthStart.format("MMM")}`;
  return STATUS_INFO[status]?.label || status;
};

// The set date used for a deduction month (null for "any day").
export const setDateFor = (version, monthStart) => {
  if (version.coverage_basis !== "SET_DATE") return null;
  const month = String(monthStart.month() + 1);
  const day = version.set_dates[version.deduction_months.indexOf(month)] || "LAST";
  return day === "LAST" ? monthStart.clone().endOf("month").startOf("day") : monthStart.clone().date(Number(day));
};

const rollsLabel = (version, monthStart) => {
  const setDate = setDateFor(version, monthStart);
  return setDate ? `On the rolls on ${setDate.format("D MMM YYYY")} (set date)` : `Employed in ${monthStart.format("MMM YYYY")} (any day)`;
};

// ---- Register: one employee in one payroll month (B5.1) ----

export const registerRow = (employee, monthKey) => {
  const monthStart = moment(`${monthKey}-01`);
  const monthEnd = monthStart.clone().endOf("month");
  // Not in this month's payroll at all.
  if (moment(employee.doj).isAfter(monthEnd) || (employee.lwd && moment(employee.lwd).isBefore(monthStart))) return null;
  const site = siteByName(employee.site);
  const version = versionOn(site, iso(monthStart));
  const wage = wageFor(employee, monthKey);
  const base = {
    ...employee,
    site_name: employee.site,
    category_label: employee.category || "—",
    month_key: monthKey,
    month_label: monthStart.format("MMM YYYY"),
    version,
    ee: null,
    er: null,
    ee_outstanding: 0,
    ee_paid_by_company: 0,
    band: null,
    rolls: null,
  };
  const finish = (status, extra = {}) => {
    const row = { ...base, ...extra, status };
    return {
      ...row,
      status_label: statusLabel(status, employee, monthStart),
      status_tone: STATUS_INFO[status]?.tone || "slate",
      total: row.ee == null ? null : row.ee + row.er,
    };
  };

  if (!version) return finish("SITE_NOT_SET_UP", { lwf_wage: wage });
  if (version.applicability === "NOT_APPLICABLE") return finish("NOT_APPLICABLE", { lwf_wage: wage });
  if (version.applicability === "EXEMPT") return finish("EXEMPT_SITE", { lwf_wage: wage });
  if (!version.deduction_months.includes(String(monthStart.month() + 1))) return finish("NOT_DEDUCTION_MONTH", { lwf_wage: lwfWageOf(version, wage), ee: 0, er: 0 });

  const setDate = setDateFor(version, monthStart);
  const onRolls = setDate ? !moment(employee.doj).isAfter(setDate) && (!employee.lwd || !moment(employee.lwd).isBefore(setDate)) : true;
  const rolls = { ok: onRolls, label: rollsLabel(version, monthStart) };
  if (!onRolls) return finish("NOT_ON_ROLLS", { lwf_wage: null, ee: 0, er: 0, rolls });

  const lwfWage = lwfWageOf(version, wage);
  const result = coverageOf(version, { category: employee.category, LWF_WAGE: lwfWage });
  const band = pickBand(version.bands, lwfWage);
  const extra = { lwf_wage: lwfWage, rolls, band, coverage: result };
  if (result.status === "HELD_MISSING_DATA") return finish("HELD_MISSING_DATA", extra);
  if (result.status === "NOT_COVERED_CATEGORY" || result.status === "NOT_COVERED_WAGE") return finish(result.status, { ...extra, ee: 0, er: 0 });
  return finish(result.status, {
    ...extra,
    ee: result.employee,
    er: result.employer,
    ee_outstanding: result.status === "EE_OUTSTANDING" ? result.employee : 0,
    ee_paid_by_company: result.status === "EE_PAID_BY_COMPANY" ? result.employee : 0,
  });
};

// ---- Employees list (LWF2): coverage for the next deduction ----

export const EMPLOYEE_STATUS = {
  covered: { label: "Covered", tone: "green" },
  not: { label: "Not covered", tone: "slate" },
  held: { label: "Held · category missing", tone: "red" },
  notsetup: { label: "Site not set up", tone: "red" },
};

export const employeeRow = (employee, today = moment()) => {
  const site = siteByName(employee.site);
  const current =
    versionOn(site, iso(today)) ||
    (site?.versions || []).filter((version) => version.status !== "Draft").sort((a, b) => a.effective_from.localeCompare(b.effective_from))[0] ||
    null;
  const base = {
    ...employee,
    site_name: employee.site,
    category_label: employee.category || "—",
    lwf_wage: employee.wage,
    site_obj: site,
    next_label: "—",
    employee_amount: null,
    employer_amount: null,
  };
  if (!current) return { ...base, status_key: "notsetup", status_label: EMPLOYEE_STATUS.notsetup.label, status_tone: "red", reason: "SITE_NOT_SET_UP" };
  if (current.applicability !== "COVERED") {
    return { ...base, version: current, status_key: "not", status_label: `Not covered · site ${current.applicability === "EXEMPT" ? "exempt" : "not applicable"}`, status_tone: "slate", reason: current.applicability };
  }
  const next = nextDeductionMonth(current, today);
  const version = versionOn(site, iso(next)) || current;
  const lwfWage = lwfWageOf(version, employee.wage);
  const result = coverageOf(version, { category: employee.category, LWF_WAGE: lwfWage });
  const common = { ...base, version, next, next_label: next.format("MMM YYYY"), lwf_wage: lwfWage, result, setDate: setDateFor(version, next) };
  if (result.status === "HELD_MISSING_DATA") return { ...common, status_key: "held", status_label: EMPLOYEE_STATUS.held.label, status_tone: "red", reason: result.status };
  if (result.status === "NOT_COVERED_CATEGORY") {
    return { ...common, status_key: "not", status_label: `Not covered · ${employee.category}`, status_tone: "slate", reason: result.status, employee_amount: 0, employer_amount: 0 };
  }
  if (result.status === "NOT_COVERED_WAGE") {
    return { ...common, status_key: "not", status_label: "Not covered · wage", status_tone: "slate", reason: result.status, employee_amount: 0, employer_amount: 0 };
  }
  return { ...common, status_key: "covered", status_label: "Covered", status_tone: "green", reason: result.status, employee_amount: result.employee, employer_amount: result.employer };
};

// Deduction history for the drawer: past finalised deduction months, then
// the next one.
export const FINALISED_MONTHS = ["2025-12"];
export const historyOf = (row) => {
  const past = FINALISED_MONTHS.map((monthKey) => registerRow(row, monthKey))
    .filter((entry) => entry && entry.status !== "NOT_DEDUCTION_MONTH")
    .map((entry) => ({
      label: entry.month_label,
      date: entry.ee != null && COVERED_STATUSES.includes(entry.status) ? `${money(entry.ee)} + ${money(entry.er)} ${entry.status === "DEDUCTED" ? "paid" : "· outstanding"}` : entry.status_label,
      done: true,
    }));
  return [...past, ...(row.next ? [{ label: row.next.format("MMM YYYY"), date: "Next", done: false }] : [])];
};

export const setDateText = (version, next) => {
  if (!version || !next) return "";
  if (version.coverage_basis !== "SET_DATE") return "Employed on any day of the deduction month";
  const month = String(next.month() + 1);
  return setDateLabel(version.set_dates[version.deduction_months.indexOf(month)] || "LAST", month);
};
